import { useMemo, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";

// S2 endpoints — editor groups and dictionary
const EDITOR_GROUPS_ENDPOINT = "my/groups/";
const GROUP_TYPES_ENDPOINT = "dictionary/GroupTypes";

/** Shorter than this is not worth a round trip — as in the notice search. */
export const MIN_GROUP_QUERY_LENGTH = 2;

/** One group, in the shape the picker draws. */
export interface EditorGroup {
	value: string;
	label: string;
	MemberCount: number;
	Icon?: string;
}

/**
 * Editor groups and group types from the S2 API.
 *
 * The group search runs as a typeahead rather than behind a Search button: the
 * endpoint narrows by `q`, React Query keys on it so a slow answer for an
 * earlier term cannot land on top of a later one, and `keepPreviousData` keeps
 * the list up while the next request runs.
 *
 * `q` is required — `my/groups/?editors_only=true` with no term is a request
 * for every group this person can edit, which is not what the picker wants and
 * not a load worth asking the API for on mount.
 */
export function useEditorGroups(adminGroups?: string[]) {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const [term, setTerm] = useState("");
	const [groupType, setGroupType] = useState("");

	// Group types — fetch once on mount
	const { data: groupTypes = [] } = useQuery({
		queryKey: ["groupTypes"],
		queryFn: async () => {
			const res = await callPymbleApi(
				instance,
				"GET",
				GROUP_TYPES_ENDPOINT
			);
			if (!res) return [];
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const groupTypesArray: any[] = [];
			for (const e of res) {
				const data = e["Data"];
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				data.forEach((type: any) => {
					groupTypesArray.push({
						id: type["LocalId"],
						value: type["Name"],
						type: type["TypeId"],
					});
				});
			}
			groupTypesArray.sort((a, b) => a.value.localeCompare(b.value));
			return groupTypesArray;
		},
		enabled: isAuthenticated,
	});

	const trimmed = term.trim();
	const longEnough = trimmed.length >= MIN_GROUP_QUERY_LENGTH;
	const adminKey = adminGroups ? adminGroups.join(",") : "";

	const query = useQuery({
		queryKey: ["editor-groups", trimmed, groupType, adminKey],
		queryFn: async (): Promise<EditorGroup[]> => {
			const endpoint =
				EDITOR_GROUPS_ENDPOINT +
				"?editors_only=true&q=" +
				encodeURIComponent(trimmed) +
				(adminKey ? "&g=" + encodeURIComponent(adminKey) : "") +
				(groupType ? "&group_type=" + encodeURIComponent(groupType) : "");

			const res = await callPymbleApi(instance, "GET", endpoint);
			if (!Array.isArray(res)) return [];

			return res
				.map((e) => ({
					value: e["_id"],
					label: e["Title"] || e["Name"]?.["Short"] || "",
					MemberCount: e["GroupMemberCount"] || e["MemberCount"] || 0,
					Icon: e["Icon"],
				}))
				.filter((g) => g.value)
				.sort((a, b) => a.label.localeCompare(b.label));
		},
		enabled: isAuthenticated && longEnough,
		placeholderData: keepPreviousData,
	});

	return {
		groupTypes,
		term,
		setTerm,
		groupType,
		setGroupType,
		longEnough,
		minQueryLength: MIN_GROUP_QUERY_LENGTH,
		groups: useMemo(() => query.data ?? [], [query.data]),
		searching: longEnough && query.isFetching,
		searchFailed: !!query.error,
	};
}
